import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "flex panel-stake flex-column sm:justify-content-between sm:flex-row xl:flex-column" }
const _hoisted_2 = { class: "flex-grow-1" }
const _hoisted_3 = { class: "caption mb-s2" }
const _hoisted_4 = { class: "xl:w-12 sm:w-4 sm:pl-s3 xl:p-s0" }

import { computed, nextTick, ref } from 'vue';
import { DEFAULT_NETWORK_ID } from '@/helpers/networkParams.helper';
import { ENABLE_FAKE_CARDANO_NETWORK } from '@/helpers/fakeCardanoNetwork';
import { toWei } from '@/sdk/utils';
import { formatNumbersMixin } from '@/mixins/format-numbers.mixin';
import { Staking } from '@/sdk/entities/staking';
import Spinner from '@/components/Spinner.vue';
import CollectModal from './CollectModal.vue';
import { useAllSteps } from '@/composables/useAllSteps';
import { useTokens } from '@/store/modules/tokens/useTokens';
import { useStakingSteps } from '@/store/modules/staking/useStakingSteps';
import { useStakingHarvest } from '@/store/modules/staking/harvest/useStakingHarvest';
import { useStakingCompound } from '@/store/modules/staking/compound/useStakingCompound';
import { useI18n } from 'vue-i18n';


export default /*#__PURE__*/_defineComponent({
  __name: 'Collect',
  props: {
    pool: null
  },
  emits: ["sendTransaction"],
  setup(__props: any, { emit: emits }: { emit: ({
  (e: 'sendTransaction'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  pool: Staking;
}

const { format } = formatNumbersMixin.methods;





const allStepsState = useAllSteps();
const { getTokenByAddressAndChainId } = useTokens();
const { stakingSteps, setStaking, $reset: resetStakingSteps } = useStakingSteps();
const { checkHarvestForBridgeFromMilkomeda, setHarvestFromPool, doHarvest } = useStakingHarvest();
const { checkCompoundForBridgeFromCardano, setCompoundFromPool, doCompound } = useStakingCompound();

const { t } = useI18n();

const isShowDialog = ref(false);
const inProgressCompound = ref(false);
const inProgressHarvest = ref(false);

const isCollectInProgress = computed(() => inProgressCompound.value || inProgressHarvest.value);
const isCollectDisabled = computed(
  () => !props.pool.earned.gt(0) || isCollectInProgress.value || allStepsState.isInProgress.state,
);
const poolToken = computed(() => {
  if (!DEFAULT_NETWORK_ID) return;
  return getTokenByAddressAndChainId(props.pool.token, +DEFAULT_NETWORK_ID);
});

const isSyrupPool = computed(() => props.pool.isSyrupPool);

const poolName = computed(() =>
  t(`yieldPool.manual.poolTitle.${isSyrupPool.value ? 'syrup' : 'blues'}.caption`, {
    name: poolToken.value?.name,
  }),
);

function onDialogOpen() {
  isShowDialog.value = true;
}
function onDialogClose() {
  isShowDialog.value = false;
}
// HARVEST
async function onHarvestWSC() {
  if (stakingSteps.isShown) {
    resetStakingSteps();
  }
  if (!poolToken.value) return;

  setStaking('manual', 'harvest');

  const amountInWei = toWei(
    props.pool.printYouEarned.toFixed(),
    poolToken.value.decimals,
  ).toString();
  checkHarvestForBridgeFromMilkomeda(amountInWei, poolToken.value);

  setHarvestFromPool(props.pool, poolToken.value);

  await nextTick();

  emits('sendTransaction');
}
async function onHarvest() {
  if (!poolToken.value) return;

  inProgressHarvest.value = true;

  try {
    setHarvestFromPool(props.pool, poolToken.value);
    await doHarvest();
  } finally {
    inProgressHarvest.value = false;
  }
}
async function onHarvestTransaction() {
  onDialogClose();

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    await onHarvestWSC();
    return;
  }

  await onHarvest();
}
// COMPOUND
async function onCompoundWSC() {
  if (!poolToken.value) return;

  if (stakingSteps.isShown) {
    resetStakingSteps();
  }

  setStaking('manual', 'compound');
  checkCompoundForBridgeFromCardano();
  setCompoundFromPool(props.pool, poolToken.value);

  await nextTick();

  emits('sendTransaction');
}
async function onCompound() {
  inProgressCompound.value = true;
  if (!poolToken.value) return;

  try {
    setCompoundFromPool(props.pool, poolToken.value);
    await doCompound();
  } finally {
    inProgressCompound.value = false;
  }
}
async function onCompoundTransaction() {
  onDialogClose();

  if (ENABLE_FAKE_CARDANO_NETWORK) {
    await onCompoundWSC();
    return;
  }

  await onCompound();
}

return (_ctx: any,_cache: any) => {
  const _component_Button = _resolveComponent("Button")!
  const _component_Dialog = _resolveComponent("Dialog")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, _toDisplayString(_unref(t)('yieldPool.manual.youEarned')), 1),
      _createElementVNode("div", null, _toDisplayString(`${_unref(format)(__props.pool.printYouEarned)} ${_unref(poolToken)?.symbol}`), 1)
    ]),
    _createElementVNode("div", _hoisted_4, [
      _createVNode(_component_Button, {
        disabled: _unref(isCollectDisabled),
        class: "w-full button-primary button-lg mt-s5 sm:mt-s0",
        onClick: onDialogOpen
      }, {
        default: _withCtx(() => [
          (_unref(isCollectInProgress))
            ? (_openBlock(), _createBlock(Spinner, {
                key: 0,
                color: "white",
                size: "24",
                class: "mr-s2"
              }))
            : _createCommentVNode("", true),
          _createTextVNode(" " + _toDisplayString(_unref(isCollectInProgress) ? _unref(t)('inProgress') : _unref(t)('yieldPool.manual.collectEarnings')), 1)
        ]),
        _: 1
      }, 8, ["disabled"])
    ]),
    _createVNode(_component_Dialog, {
      visible: isShowDialog.value,
      "onUpdate:visible": _cache[0] || (_cache[0] = ($event: any) => ((isShowDialog).value = $event)),
      header: _unref(t)('yieldPool.collectConfirmDialog.header', { poolName: _unref(poolName) }),
      dismissableMask: "",
      modal: true
    }, {
      default: _withCtx(() => [
        _createVNode(CollectModal, {
          cancel: onDialogClose,
          compound: onCompoundTransaction,
          harvest: onHarvestTransaction,
          amount: __props.pool.printYouEarned,
          token: _unref(poolToken)!,
          amountUsd: __props.pool.printYouEarnedUSD,
          "in-progress-compound": inProgressCompound.value,
          "in-progress-harvest": inProgressHarvest.value,
          "is-syrup-pool": _unref(isSyrupPool)
        }, null, 8, ["amount", "token", "amountUsd", "in-progress-compound", "in-progress-harvest", "is-syrup-pool"])
      ]),
      _: 1
    }, 8, ["visible", "header"])
  ]))
}
}

})